import { isVentoraDevice } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';

export type VentoraSensorKind = 'temperature' | 'humidity' | 'illuminance';

type SensorChannelSettings = Record<string, number | string | undefined>;

export const DEFAULT_TEMP_OFFSET_C = 0;
export const DEFAULT_TEMP_REPORT_THR_C = 0.5;
export const DEFAULT_HUMIDITY_OFFSET = 0;
export const DEFAULT_HUMIDITY_REPORT_THR = 5;
export const DEFAULT_ILLUMINANCE_DARK_THR = 0;
export const DEFAULT_ILLUMINANCE_BRIGHT_THR = 0;

export const SENSOR_NAME_KIND_BY_COMPONENT: Record<string, VentoraSensorKind> = {
	temperature_sensor_name_g2: 'temperature',
	humidity_sensor_name_g2: 'humidity',
	illuminance_sensor_name_g2: 'illuminance',
};

export const VENTORA_SENSOR_GROUP_KIND_BY_COMPONENT: Record<string, VentoraSensorKind> = {
	ventora_temperature_settings: 'temperature',
	ventora_humidity_settings: 'humidity',
	ventora_illuminance_settings: 'illuminance',
};

export const resolveVentoraSensorKindFromComponent = (
	componentKey: string,
): VentoraSensorKind | undefined =>
	SENSOR_NAME_KIND_BY_COMPONENT[componentKey] ?? VENTORA_SENSOR_GROUP_KIND_BY_COMPONENT[componentKey];

export const SENSOR_SET_CONFIG_METHOD: Record<VentoraSensorKind, string> = {
	temperature: 'Temperature.SetConfig',
	humidity: 'Humidity.SetConfig',
	illuminance: 'Illuminance.SetConfig',
};

export const getSensorComponentKey = (kind: VentoraSensorKind): string => `${kind}:0`;

export const readSensorComponentName = (
	settings: Record<string, SensorChannelSettings | undefined> | undefined,
	kind: VentoraSensorKind,
): string => {
	const name = settings?.[getSensorComponentKey(kind)]?.name;
	if (name === null || name === undefined) {
		return '';
	}
	return String(name);
};

/** Ventora (S4PB-0916176901) FW expects offset + report threshold in one SetConfig call. */
export const usesVentoraMergedSensorSetConfig = (device: Device): boolean =>
	isVentoraDevice(device.type);

export const buildTemperatureSetConfigParams = (
	settings: Record<string, SensorChannelSettings | undefined>,
	channel: number,
	updates: Partial<{ offset_C: number; report_thr_C: number }>,
) => {
	const temperatureChannel = settings?.['temperature:0'] ?? {};
	return {
		id: channel,
		config: {
			offset_C: Number(updates.offset_C ?? temperatureChannel.offset_C ?? DEFAULT_TEMP_OFFSET_C),
			report_thr_C: Number(
				updates.report_thr_C ?? temperatureChannel.report_thr_C ?? DEFAULT_TEMP_REPORT_THR_C,
			),
		},
	};
};

export const buildHumiditySetConfigParams = (
	settings: Record<string, SensorChannelSettings | undefined>,
	channel: number,
	updates: Partial<{ offset: number; report_thr: number }>,
) => {
	const humidityChannel = settings?.['humidity:0'] ?? {};
	return {
		id: channel,
		config: {
			offset: Number(updates.offset ?? humidityChannel.offset ?? DEFAULT_HUMIDITY_OFFSET),
			report_thr: Number(
				updates.report_thr ?? humidityChannel.report_thr ?? DEFAULT_HUMIDITY_REPORT_THR,
			),
		},
	};
};

export const buildIlluminanceSetConfigParams = (
	settings: Record<string, SensorChannelSettings | undefined>,
	channel: number,
	updates: Partial<{ dark_thr: number; bright_thr: number }>,
) => {
	const illuminanceChannel = settings?.['illuminance:0'] ?? {};
	return {
		id: channel,
		config: {
			dark_thr: Number(
				updates.dark_thr ?? illuminanceChannel.dark_thr ?? DEFAULT_ILLUMINANCE_DARK_THR,
			),
			bright_thr: Number(
				updates.bright_thr ?? illuminanceChannel.bright_thr ?? DEFAULT_ILLUMINANCE_BRIGHT_THR,
			),
		},
	};
};

type G2SensorSetConfigParams = {
	id: number;
	config: Record<string, number>;
};

/** Offset save request; Ventora merges offset with report threshold in one SetConfig call. */
export const resolveSensorOffsetSetConfig = (
	device: Device,
	setting: string,
	settings: Record<string, SensorChannelSettings | undefined>,
	channel: number,
	valueAttr: string,
	value: number,
): { method: string; params: G2SensorSetConfigParams } => {
	if (usesVentoraMergedSensorSetConfig(device)) {
		if (setting === 'temperature') {
			return {
				method: SENSOR_SET_CONFIG_METHOD.temperature,
				params: buildTemperatureSetConfigParams(settings, channel, {
					offset_C: value,
				}),
			};
		}
		return {
			method: SENSOR_SET_CONFIG_METHOD.humidity,
			params: buildHumiditySetConfigParams(settings, channel, { offset: value }),
		};
	}

	return {
		method: `${setting}.setconfig`,
		params: {
			id: channel,
			config: { [valueAttr]: value },
		},
	};
};
